<template>
  <div class="metricas-ces pa-2">
    <!-- ─── Header ─── -->
    <div class="ces-header mb-4">
      <h2 class="ces-title">CES <span class="ces-title__sub">(Customer Effort Score)</span></h2>
      <p class="ces-description mt-1">
        En esta sección se mide el esfuerzo del cliente del <b>1 al 5</b>, donde un valor
        más bajo indica una mejor experiencia (menor esfuerzo). Corresponde al CES evaluado en el
        <b>flujo de reembolsos</b>. Contamos con registros a partir del <b>1 de marzo de 2025</b>,
        fecha en que esta métrica comenzó a funcionar.
      </p>
    </div>

    <!-- ─── Filtros de fecha ─── -->
    <v-card outlined class="mb-5 pa-4">
      <v-row align="center" no-gutters>
        <v-col cols="auto" class="mr-3">
          <v-menu
            v-model="fromDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :z-index="9999"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                label="Fecha inicio"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                style="max-width: 165px;"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              :min="CES_MIN_DATE"
              :max="toDate"
              @input="fromDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-col cols="auto" class="mr-3">
          <v-menu
            v-model="toDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :z-index="9999"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                label="Fecha fin"
                prepend-inner-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                style="max-width: 165px;"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              :min="fromDate"
              :max="today"
              @input="toDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-btn color="primary" :loading="loadingGeneral || loadingB2C || loadingCoverage" @click="loadAll">
            <v-icon left>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- ─── Cards generales ─── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <v-card outlined class="ces-card">
          <div class="ces-card__label">CES total general</div>
          <div class="ces-card__value" v-if="!loadingGeneral">
            {{ generalMetrics.avgCES !== null ? generalMetrics.avgCES : '—' }}
            <span class="ces-card__scale">/ 5</span>
          </div>
          <v-skeleton-loader v-else type="text" class="mt-2" />
          <div class="ces-card__hint">Promedio general de todos los reembolsos</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card outlined class="ces-card">
          <div class="ces-card__label">Número de respuestas totales</div>
          <div class="ces-card__value" v-if="!loadingGeneral">
            {{ generalMetrics.totalResponses.toLocaleString('es-CL') }}
          </div>
          <v-skeleton-loader v-else type="text" class="mt-2" />
          <div class="ces-card__hint">Número total de evaluaciones CES recibidas</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ─── Sección B2B ─── -->
    <div class="ces-section-title mb-3">B2B</div>

    <v-row>
      <!-- Lista de empresas con checkboxes -->
      <v-col cols="12" md="3">
        <v-card outlined class="pa-3" style="max-height: 400px; overflow-y: auto;">
          <div class="ces-companies-title mb-2">
            Empresas
            <v-btn
              x-small
              text
              color="primary"
              class="ml-1"
              @click="toggleAllCompanies"
            >
              {{ allSelected ? 'Ninguna' : 'Todas' }}
            </v-btn>
          </div>

          <v-skeleton-loader v-if="loadingCompanies" type="list-item@6" />

          <template v-else>
            <div
              v-for="company in previewCompanies"
              :key="company.id"
              class="ces-company-row"
            >
              <v-checkbox
                v-model="selectedCompanyIds"
                :value="company.id"
                :label="company.name"
                hide-details
                dense
                class="mt-0 pt-0"
              ></v-checkbox>
            </div>

            <v-btn
              v-if="hasMoreCompanies"
              small
              text
              color="primary"
              class="mt-2"
              @click="showAllCompaniesDialog = true"
            >
              Ver más ({{ allCompanies.length - PREVIEW_COUNT }} más)
            </v-btn>
          </template>
        </v-card>

        <v-btn
          color="primary"
          small
          class="mt-3"
          :loading="loadingB2B"
          :disabled="selectedCompanyIds.length === 0"
          @click="loadB2BData"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar gráfico
        </v-btn>
      </v-col>

      <!-- Gráfico B2B -->
      <v-col cols="12" md="9">
        <v-card outlined class="pa-4">
          <div class="ces-chart-title mb-3">Evolución CES — B2B</div>
          <div v-if="selectedCompanyIds.length === 0" class="text-center grey--text py-10">
            <v-icon large color="grey lighten-2">mdi-office-building-outline</v-icon>
            <div class="mt-2">Selecciona al menos una empresa para ver el gráfico</div>
          </div>
          <CESLineChart
            v-else
            :series="b2bSeries"
            :height="260"
          >
            <template v-slot:empty>
              Sin datos de CES para las empresas y período seleccionados
            </template>
          </CESLineChart>
          <v-progress-linear v-if="loadingB2B" indeterminate color="primary" class="mt-2" />
        </v-card>
      </v-col>
    </v-row>

    <!-- ─── Sección B2C ─── -->
    <div class="ces-section-title mt-6 mb-3">B2C</div>

    <v-card outlined class="pa-4">
      <div class="ces-chart-title mb-3">Evolución CES — B2C por país</div>
      <v-progress-linear v-if="loadingB2C" indeterminate color="primary" class="mb-2" />
      <CESLineChart
        :series="b2cSeries"
        :height="260"
      >
        <template v-slot:empty>
          Sin datos de CES B2C para el período seleccionado
        </template>
      </CESLineChart>
    </v-card>

    <!-- ─── Sección Por cobertura ─── -->
    <div class="ces-section-title mt-6 mb-3">Por cobertura</div>

    <v-card outlined class="pa-4">
      <div class="ces-chart-title mb-3">Ranking CES por tipo de cobertura</div>
      <v-progress-linear v-if="loadingCoverage" indeterminate color="primary" class="mb-3" />

      <div v-if="!loadingCoverage && coverageData.length === 0" class="text-center grey--text py-10">
        <v-icon large color="grey lighten-2">mdi-shield-search</v-icon>
        <div class="mt-2">Sin datos de CES por cobertura para el período seleccionado</div>
      </div>

      <v-simple-table v-else-if="!loadingCoverage" dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left ces-coverage-th">#</th>
              <th class="text-left ces-coverage-th">Cobertura</th>
              <th class="text-left ces-coverage-th">Promedio CES</th>
              <th class="text-left ces-coverage-th">Respuestas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in visibleCoverageData" :key="item.serviceId">
              <td class="ces-coverage-rank">{{ index + 1 }}</td>
              <td>{{ item.serviceTitle }}</td>
              <td>
                <span class="ces-coverage-avg">{{ item.avgCES }}</span>
                <span class="grey--text text-caption ml-1">/ 5</span>
              </td>
              <td class="grey--text text--darken-1">{{ item.count.toLocaleString('es-CL') }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div v-if="!loadingCoverage && coverageData.length > COVERAGE_PREVIEW_COUNT" class="mt-3">
        <v-btn small text color="primary" @click="showAllCoverage = !showAllCoverage">
          {{ showAllCoverage ? 'Ver menos' : `Ver más (${coverageData.length - COVERAGE_PREVIEW_COUNT} más)` }}
        </v-btn>
      </div>
    </v-card>

    <!-- ─── Diálogo Ver más empresas ─── -->
    <v-dialog v-model="showAllCompaniesDialog" max-width="700" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Todas las empresas</span>
          <v-btn icon @click="showAllCompaniesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 60vh; overflow-y: auto;">
          <div
            v-for="(companies, type) in companiesByType"
            :key="type"
            class="mb-4"
          >
            <div class="ces-type-label mb-1">{{ companyTypeLabel(type) }}</div>
            <v-row dense>
              <v-col
                v-for="company in companies"
                :key="company.id"
                cols="12"
                sm="6"
              >
                <v-checkbox
                  v-model="selectedCompanyIds"
                  :value="company.id"
                  :label="company.name"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                ></v-checkbox>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-card-actions>
          <span class="grey--text text--darken-1 text-body-2 ml-2">
            {{ selectedCompanyIds.length }} empresa(s) seleccionada(s)
          </span>
          <v-spacer></v-spacer>
          <v-btn text @click="showAllCompaniesDialog = false">Cerrar</v-btn>
          <v-btn
            color="primary"
            @click="showAllCompaniesDialog = false; loadB2BData()"
          >
            Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CesService from '../services/ces.service';
import CESLineChart from '../components/CESLineChart.vue';

const CHART_COLORS = [
  '#104B94', '#4CAF50', '#FF5722', '#9C27B0',
  '#FF9800', '#00BCD4', '#E91E63', '#607D8B',
  '#795548', '#3F51B5', '#8BC34A', '#F44336',
  '#009688', '#FFC107', '#673AB7', '#03A9F4',
];

/** Series of the B2C chart per country; Chile and Perú keep their colors. */
const COUNTRY_SERIES = {
  CL: { name: 'Chile', color: CHART_COLORS[0] },
  PE: { name: 'Perú', color: CHART_COLORS[11] },
  MX: { name: 'México', color: CHART_COLORS[1] },
};
const COUNTRY_ORDER = ['CL', 'PE', 'MX'];

const PREVIEW_COUNT = 10;
const COVERAGE_PREVIEW_COUNT = 10;

const COMPANY_TYPE_LABELS = {
  assistance: 'Asistencia',
  eb: 'Employee Benefit',
  dxp: 'Dcto x Planilla',
  broker: 'Broker',
  'broker-dxp': 'Broker-DxP',
  'broker-eb': 'Broker-EB',
};

const CES_MIN_DATE = '2025-03-01';

function defaultFromDate() {
  return '2025-01-01';
}

function defaultToDate() {
  return new Date().toISOString().slice(0, 10);
}

export default {
  name: 'MetricasCES',
  components: { CESLineChart },

  data() {
    return {
      PREVIEW_COUNT,
      COVERAGE_PREVIEW_COUNT,
      CES_MIN_DATE,
      today: defaultToDate(),
      fromDate: defaultFromDate(),
      toDate: defaultToDate(),
      fromDateMenu: false,
      toDateMenu: false,

      loadingGeneral: false,
      loadingCompanies: false,
      loadingB2B: false,
      loadingB2C: false,
      loadingCoverage: false,

      generalMetrics: { avgCES: null, totalResponses: 0 },
      allCompanies: [],
      selectedCompanyIds: [],
      b2bRawData: [],
      b2cRawData: [],
      coverageData: [],
      showAllCoverage: false,

      showAllCompaniesDialog: false,
    };
  },

  computed: {
    previewCompanies() {
      return [...this.allCompanies]
        .sort((a, b) => a.name.localeCompare(b.name, 'es'))
        .slice(0, this.PREVIEW_COUNT);
    },
    hasMoreCompanies() {
      return this.allCompanies.length > this.PREVIEW_COUNT;
    },
    allSelected() {
      return (
        this.allCompanies.length > 0 &&
        this.selectedCompanyIds.length === this.allCompanies.length
      );
    },

    visibleCoverageData() {
      return this.showAllCoverage
        ? this.coverageData
        : this.coverageData.slice(0, this.COVERAGE_PREVIEW_COUNT);
    },

    companiesByType() {
      const grouped = {};
      for (const c of this.allCompanies) {
        const key = c.type || 'other';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(c);
      }
      return grouped;
    },

    /** Transform raw B2B data into CESLineChart series format */
    b2bSeries() {
      // Sort company IDs to get a stable, collision-free color assignment
      const sortedIds = [...new Set(this.b2bRawData.map(r => r.companyId))].sort();
      const colorMap = {};
      sortedIds.forEach((id, i) => {
        colorMap[id] = CHART_COLORS[i % CHART_COLORS.length];
      });

      const map = {};
      for (const row of this.b2bRawData) {
        if (!map[row.companyId]) {
          map[row.companyId] = {
            name: row.companyName,
            color: colorMap[row.companyId],
            data: [],
          };
        }
        map[row.companyId].data.push({
          period: row.period,
          avgCES: row.avgCES,
          count: row.count,
        });
      }
      return Object.values(map);
    },

    /**
     * Transform raw B2C data into CESLineChart series format, one series per
     * country with answers. Spec 084: only CL and PE were drawn, so Mexican
     * answers were dropped. Colors come from the chart palette, not new hexes.
     */
    b2cSeries() {
      const isos = [...new Set(this.b2cRawData.map(r => r.countryIso))]
        .sort((a, b) => COUNTRY_ORDER.indexOf(a) - COUNTRY_ORDER.indexOf(b));
      return isos.map((iso, i) => ({
        name: COUNTRY_SERIES[iso] ? COUNTRY_SERIES[iso].name : iso,
        color: COUNTRY_SERIES[iso] ? COUNTRY_SERIES[iso].color : CHART_COLORS[(i + 4) % CHART_COLORS.length],
        data: this.b2cRawData
          .filter(r => r.countryIso === iso)
          .map(r => ({ period: r.period, avgCES: r.avgCES, count: r.count })),
      }));
    },
  },

  mounted() {
    this.loadAll();
  },

  methods: {
    async loadAll() {
      await Promise.all([
        this.loadGeneralMetrics(),
        this.loadCompanies(),
        this.loadB2CData(),
        this.loadCoverageData(),
      ]);
      if (this.selectedCompanyIds.length > 0) {
        await this.loadB2BData();
      }
    },

    async loadGeneralMetrics() {
      this.loadingGeneral = true;
      const res = await CesService.getGeneralMetrics(this.fromDate, this.toDate);
      if (res.status === 'ok') this.generalMetrics = res.data;
      this.loadingGeneral = false;
    },

    async loadCompanies() {
      this.loadingCompanies = true;
      const res = await CesService.getB2BCompanies();
      if (res.status === 'ok') this.allCompanies = res.data;
      this.loadingCompanies = false;
    },

    async loadB2BData() {
      if (this.selectedCompanyIds.length === 0) return;
      this.loadingB2B = true;
      const res = await CesService.getB2BCESOverTime(
        this.fromDate,
        this.toDate,
        this.selectedCompanyIds
      );
      if (res.status === 'ok') this.b2bRawData = res.data;
      this.loadingB2B = false;
    },

    async loadB2CData() {
      this.loadingB2C = true;
      const res = await CesService.getB2CCESOverTime(this.fromDate, this.toDate);
      if (res.status === 'ok') this.b2cRawData = res.data;
      this.loadingB2C = false;
    },

    async loadCoverageData() {
      this.loadingCoverage = true;
      const res = await CesService.getCESByCoverage(this.fromDate, this.toDate);
      if (res.status === 'ok') this.coverageData = res.data;
      this.loadingCoverage = false;
    },

    toggleAllCompanies() {
      if (this.allSelected) {
        this.selectedCompanyIds = [];
      } else {
        this.selectedCompanyIds = this.allCompanies.map(c => c.id);
      }
    },

    companyTypeLabel(type) {
      return COMPANY_TYPE_LABELS[type] || type;
    },
  },
};
</script>

<style scoped>
.metricas-ces {
  max-width: 1200px;
}

/* Header */
.ces-header {
  border: none;
  border-left: none;
  padding-left: 0;
}

.ces-title {
  font-family: Inter, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1B1D21;
  margin: 0;
}

.ces-title__sub {
  font-size: 16px;
  font-weight: 400;
  color: #8593A3;
}

.ces-description {
  font-size: 13px;
  color: #8593A3;
  margin: 0;
  width: 100%;
}

/* Cards generales */
.ces-card {
  padding: 16px;
  height: 100%;
}

.ces-card__label {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1B1D21;
  margin-bottom: 8px;
}

.ces-card__value {
  font-size: 32px;
  font-weight: 700;
  color: #104B94;
  line-height: 1;
}

.ces-card__scale {
  font-size: 14px;
  font-weight: 400;
  color: #8593A3;
}

.ces-card__hint {
  font-size: 11px;
  color: #8593A3;
  margin-top: 6px;
}

/* Section title */
.ces-section-title {
  font-family: Inter, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1B1D21;
  display: block;
  text-decoration: none;
  border: none;
}

/* Chart title */
.ces-chart-title {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Companies list */
.ces-companies-title {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #1B1D21;
  display: flex;
  align-items: center;
}

.ces-company-row {
  padding: 2px 0;
}

/* Coverage table */
.ces-coverage-th {
  color: #104B94 !important;
  font-weight: 700 !important;
  font-size: 14px !important;
}

.ces-coverage-rank {
  color: #8593A3;
  width: 32px;
}

.ces-coverage-avg {
  font-weight: 700;
  color: #104B94;
}

/* Dialog type label */
.ces-type-label {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #104B94;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}
</style>
